// Places ticks from what is really on the page. The AI's coordinates are only estimates, so a
// question's printed row (or AI box on a scan) sets the search area and the learner's ink inside
// it sets the final position. All values are normalized page coordinates unless named *Px.

const INK_LUMINANCE = 190;
const INK_SATURATION = 60;
const LINE_SHARE = 0.8;
const MIN_LINE_GAP_PX = 3;
const MIN_LINE_PIXELS = 40;
const MAX_RULE_THICKNESS_PX = 5;
const MIN_PAGE_PEN_PIXELS = 400;
const MAX_INK_SHARE = 0.4;
const MIN_LINE_SHARE = 0.15;
const RULE_MIN_SHARE = 0.2;
const MAX_ROW_HEIGHT = 0.12;
const MAX_ROW_DISTANCE = 0.05;
const MIN_TICK = 0.011;
const MAX_TICK = 0.03;

/** 1 where a pixel is pen, pencil or print: dark, or clearly coloured like red pen. */
export function inkMaskFromRgba(rgba, width, height) {
  const mask = new Uint8Array(width * height);
  for (let index = 0; index < mask.length; index += 1) {
    const r = rgba[index * 4]; const g = rgba[index * 4 + 1]; const b = rgba[index * 4 + 2];
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    if (luminance < INK_LUMINANCE || Math.max(r, g, b) - Math.min(r, g, b) > INK_SATURATION) mask[index] = 1;
  }
  return mask;
}

/** 1 only where a pixel is coloured. On scans this separates blue or red pen from black print. */
export function penMaskFromRgba(rgba, width, height) {
  const mask = new Uint8Array(width * height);
  for (let index = 0; index < mask.length; index += 1) {
    const r = rgba[index * 4]; const g = rgba[index * 4 + 1]; const b = rgba[index * 4 + 2];
    if (Math.max(r, g, b) - Math.min(r, g, b) > INK_SATURATION) mask[index] = 1;
  }
  return mask;
}

/** Heights of the long ruled lines on a page, which are the row edges of tables and answer boxes. */
export function findRuledLines(mask, width, height) {
  const minimum = width * RULE_MIN_SHARE;
  const isRule = new Array(height).fill(false);
  for (let y = 0; y < height; y += 1) {
    let best = 0; let current = 0; let gap = 0;
    for (let x = 0; x < width; x += 1) {
      if (mask[y * width + x]) { current += gap + 1; gap = 0; best = Math.max(best, current); }
      else if (gap < 2) gap += 1;
      else { current = 0; gap = 0; }
    }
    isRule[y] = best >= minimum;
  }
  return runs(isRule, 2).map(([top, bottom]) => ({ top: top / height, bottom: (bottom + 1) / height }));
}

/** The row between two ruled lines that holds a height, or null when it is not a clear row. */
export function rowBandAt(rules, y) {
  const above = rules.filter(rule => rule.bottom <= y).pop();
  const below = rules.find(rule => rule.top >= y);
  if (!above || !below || below.top - above.bottom > MAX_ROW_HEIGHT) return null;
  return { left: 0.02, right: 0.98, top: above.bottom + 0.001, bottom: below.top - 0.001 };
}

/**
 * True when side walls close a row, as in a table or answer box. Writing on open lined paper sits
 * on and across its lines, so the gap between those lines is not a row.
 */
export function isEnclosedRow(ruleMask, width, height, band) {
  const x0 = Math.max(0, Math.floor(band.left * width)); const x1 = Math.min(width, Math.ceil(band.right * width));
  const y0 = Math.max(0, Math.floor(band.top * height)); const y1 = Math.min(height, Math.ceil(band.bottom * height));
  if (y1 - y0 < 8) return false;
  const isWall = [];
  for (let x = x0; x < x1; x += 1) {
    let total = 0;
    for (let y = y0; y < y1; y += 1) {
      const at = y * width + x;
      if (ruleMask[at] || ruleMask[at - 1] || ruleMask[at + 1]) total += 1;
    }
    isWall.push(total >= (y1 - y0) * LINE_SHARE);
  }
  return runs(isWall, 1).length >= 2;
}

/** Whether a page has coloured pen on it at all, which makes pen colour trustworthy there. */
export function hasPenInk(penMask) {
  let total = 0;
  for (let index = 0; index < penMask.length; index += 1) {
    total += penMask[index];
    if (total >= MIN_PAGE_PEN_PIXELS) return true;
  }
  return false;
}

/** Top-to-bottom area to search: closed table rows when there are any, else the marks' own area. */
export function scanSearchArea(ruleMask, width, height, rules, area) {
  const middle = (area.top + area.bottom) / 2;
  const enclosedAt = y => {
    const row = rowBandAt(rules, y);
    return row && isEnclosedRow(ruleMask, width, height, row) ? row : null;
  };
  const first = enclosedAt(area.top + 0.002);
  const last = enclosedAt(area.bottom - 0.002);
  if (first && last) return { ...first, bottom: last.bottom };
  return enclosedAt(middle) || { left: 0.02, right: 0.98, top: area.top - 0.008, bottom: area.bottom + 0.008 };
}

/** Every closed table or box row on a page, top to bottom. */
export function enclosedRows(ruleMask, width, height, rules) {
  const rows = [];
  for (let index = 0; index < rules.length - 1; index += 1) {
    const row = rowBandAt(rules, (rules[index].bottom + rules[index + 1].top) / 2);
    if (row && isEnclosedRow(ruleMask, width, height, row)) rows.push(row);
  }
  return rows;
}

const compareQuestions = (a, b) => {
  const left = a.split('.'); const right = b.split('.');
  for (let part = 0; part < Math.max(left.length, right.length); part += 1) {
    const order = (left[part] ?? '').localeCompare(right[part] ?? '', undefined, { numeric: true });
    if (order) return order;
  }
  return 0;
};

/**
 * Gives each question its own table row. AI heights are rough and can put two questions in one
 * tight row, but questions run in order down a page, so rows are handed out in question order
 * and the AI's height only picks the nearest row still free. Answers spanning rows keep them all.
 */
export function assignRowsInOrder(groups, rows) {
  const assigned = new Map();
  let next = 0;
  for (const group of [...groups].sort((a, b) => compareQuestions(a.key, b.key))) {
    const middle = (group.top + group.bottom) / 2;
    let best = -1; let bestDistance = MAX_ROW_DISTANCE;
    for (let index = next; index < rows.length; index += 1) {
      const distance = Math.abs((rows[index].top + rows[index].bottom) / 2 - middle);
      if (distance < bestDistance) { best = index; bestDistance = distance; }
    }
    if (best < 0) continue;
    let last = best;
    const pointsTop = Math.min(...group.pointHeights); const pointsBottom = Math.max(...group.pointHeights);
    const spansRows = rows[best].bottom - rows[best].top < pointsBottom - pointsTop;
    if (spansRows) {
      const inside = rows.map((row, index) => (row.top < pointsBottom && row.bottom > pointsTop && index >= next ? index : -1)).filter(index => index >= 0);
      if (inside.length) { best = inside[0]; last = inside[inside.length - 1]; }
    }
    assigned.set(group, { ...rows[best], bottom: rows[last].bottom });
    next = last + 1;
  }
  return assigned;
}

/** Answers sit below their question, so these are the next closed rows under a search area. */
export function rowsBelow(ruleMask, width, height, rules, y, reach = 0.08) {
  const found = [];
  for (const rule of rules) {
    if (rule.bottom < y || rule.bottom > y + reach) continue;
    const row = rowBandAt(rules, rule.bottom + 0.002);
    if (row && isEnclosedRow(ruleMask, width, height, row) && !found.some(other => other.top === row.top)) found.push(row);
  }
  return found;
}

/** Clears printed words so only handwriting (and table rules) remain. */
export function clearPrintedText(mask, width, height, items = []) {
  for (const item of items) {
    const left = Math.max(0, Math.floor(item.x * width) - 1);
    const right = Math.min(width, Math.ceil(item.right * width) + 1);
    const top = Math.max(0, Math.floor((item.cy - item.h * 0.6) * height));
    const bottom = Math.min(height, Math.ceil((item.cy + item.h * 0.6) * height));
    for (let y = top; y < bottom; y += 1) mask.fill(0, y * width + left, y * width + right);
  }
  return mask;
}

/** Splits a list of on/off slots into runs, joining runs separated by fewer than minGap slots. */
function runs(values, minGap) {
  const found = [];
  let start = -1; let end = -1;
  values.forEach((on, index) => {
    if (!on) return;
    if (start >= 0 && index - end > minGap) { found.push([start, end]); start = -1; }
    if (start < 0) start = index;
    end = index;
  });
  if (start >= 0) found.push([start, end]);
  return found;
}

/**
 * Finds written answers in a search area: table cells left to right, then written lines top to
 * bottom. Long ruled lines are ignored and vertical rules separate cells.
 */
export function findInkUnits(mask, width, height, band, ruleMask = mask) {
  const x0 = Math.max(0, Math.floor(band.left * width)); const x1 = Math.min(width, Math.ceil(band.right * width));
  const y0 = Math.max(0, Math.floor(band.top * height)); const y1 = Math.min(height, Math.ceil(band.bottom * height));
  const regionWidth = x1 - x0; const regionHeight = y1 - y0;
  if (regionWidth < 4 || regionHeight < 4) return [];

  const local = new Uint8Array(regionWidth * regionHeight);
  const ruleLocal = new Uint8Array(regionWidth * regionHeight);
  for (let y = 0; y < regionHeight; y += 1) {
    local.set(mask.subarray((y + y0) * width + x0, (y + y0) * width + x1), y * regionWidth);
    ruleLocal.set(ruleMask.subarray((y + y0) * width + x0, (y + y0) * width + x1), y * regionWidth);
  }
  // Scans lean slightly, so a vertical rule is measured across three neighbouring columns.
  const countColumn = column => {
    let total = 0;
    for (let y = 0; y < regionHeight; y += 1) {
      const at = y * regionWidth + column;
      if (ruleLocal[at] || (column > 0 && ruleLocal[at - 1]) || (column < regionWidth - 1 && ruleLocal[at + 1])) total += 1;
    }
    return total;
  };
  const countRow = row => { let total = 0; for (let x = 0; x < regionWidth; x += 1) total += local[row * regionWidth + x]; return total; };

  // A ruled line is erased by its length on the page, because scan noise can tint part of one and
  // leave pieces in the pen mask. One row above and below catches a slightly leaning line.
  const longestRun = row => {
    let best = 0; let current = 0;
    for (let x = 0; x < regionWidth; x += 1) {
      current = ruleLocal[row * regionWidth + x] ? current + 1 : 0;
      best = Math.max(best, current);
    }
    return best;
  };
  const longRows = Array.from({ length: regionHeight }, (_, y) => countRow(y) >= regionWidth * LINE_SHARE || longestRun(y) >= width * RULE_MIN_SHARE);
  // Ruled lines are thin. A thick band this long is writing or a shadow, so it stays.
  for (const [top, bottom] of runs(longRows, 1)) {
    if (bottom - top >= MAX_RULE_THICKNESS_PX) continue;
    local.fill(0, Math.max(0, top - 1) * regionWidth, Math.min(regionHeight, bottom + 2) * regionWidth);
  }
  const rules = [];
  for (let x = 0; x < regionWidth; x += 1) {
    if (regionHeight >= 8 && countColumn(x) >= regionHeight * LINE_SHARE) {
      rules.push(x);
      for (let y = 0; y < regionHeight; y += 1) local[y * regionWidth + x] = 0;
    }
  }

  // A photo's shadow or a dark scan reads as ink everywhere, which says nothing about position.
  if (local.reduce((total, pixel) => total + pixel, 0) > local.length * MAX_INK_SHARE) return [];

  const edges = [-1, ...rules, regionWidth];
  const units = [];
  for (let edge = 0; edge < edges.length - 1; edge += 1) {
    const cellLeft = edges[edge] + 1; const cellRight = edges[edge + 1];
    if (cellRight - cellLeft < 3) continue;
    const rowInk = new Array(regionHeight).fill(0);
    for (let y = 0; y < regionHeight; y += 1) {
      for (let x = cellLeft; x < cellRight; x += 1) rowInk[y] += local[y * regionWidth + x];
    }
    const cellUnits = [];
    for (const [top, bottom] of runs(rowInk.map(count => count > 0), MIN_LINE_GAP_PX)) {
      let left = cellRight; let right = cellLeft; let pixels = 0;
      for (let y = top; y <= bottom; y += 1) {
        for (let x = cellLeft; x < cellRight; x += 1) {
          if (!local[y * regionWidth + x]) continue;
          pixels += 1; left = Math.min(left, x); right = Math.max(right, x);
        }
      }
      if (pixels < MIN_LINE_PIXELS) continue;
      cellUnits.push({ left: (x0 + left) / width, right: (x0 + right + 1) / width,
        top: (y0 + top) / height, bottom: (y0 + bottom + 1) / height,
        cellRight: (x0 + cellRight) / width, pixels });
    }
    // The tail of a g or y below a line is not a line of its own.
    const most = Math.max(0, ...cellUnits.map(unit => unit.pixels));
    units.push(...cellUnits.filter(unit => unit.pixels >= most * MIN_LINE_SHARE));
  }
  return units;
}

/** Which unit each of `count` marks belongs to. A single mark goes on the final written line. */
export function assignMarksToUnits(unitCount, count) {
  return Array.from({ length: count }, (_, mark) => Math.max(0, Math.ceil(((mark + 1) * unitCount) / count) - 1));
}

/** Tick height follows the writing, so a tight table row gets a smaller tick. */
export function tickHeightFor(units) {
  const heights = units.map(unit => unit.bottom - unit.top).sort((a, b) => a - b);
  const typical = heights[Math.floor(heights.length / 2)] || MAX_TICK;
  return Math.min(MAX_TICK, Math.max(MIN_TICK, typical * 1.05));
}

/** Tick centres: after each answer's ink, side by side when marks share an answer, inside the cell. */
export function tickPositions(units, count, pageAspect = 1.4) {
  const height = tickHeightFor(units);
  const tickWidth = (height * pageAspect) * 0.9;
  const slots = new Map();
  return assignMarksToUnits(units.length, count).map(unitIndex => {
    const unit = units[unitIndex];
    const slot = slots.get(unitIndex) || 0;
    slots.set(unitIndex, slot + 1);
    const wanted = unit.right + tickWidth * (0.6 + slot);
    const limit = (unit.cellRight ?? 1) - tickWidth * 0.5;
    const x = Math.min(0.985, Math.max(unit.left, slot === 0 ? Math.min(wanted, Math.max(limit, unit.right)) : wanted));
    return { x, y: (unit.top + unit.bottom) / 2, tickHeight: height, box: unit };
  });
}

